import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { FulfillmentStage } from '../../shared/types';
import { Badge, Button, Card, LoadingScreen } from '../components/Ui';
import { useDeliveryOrder, useDeliveryTransition } from '../hooks/businessData';

const stageLabels: Record<FulfillmentStage, string> = {
  kitchen: 'En cocina',
  delivery_prep: 'En preparación',
  ready: 'Listo para retirar',
  transit: 'En reparto',
  delivered: 'Entregado',
  handed_off: 'Entregado en mostrador',
  cancelled: 'Cancelado',
};

const stageTone: Record<FulfillmentStage, 'amber' | 'blue' | 'green' | 'red' | 'purple'> = {
  kitchen: 'blue',
  delivery_prep: 'blue',
  ready: 'amber',
  transit: 'purple',
  delivered: 'green',
  handed_off: 'green',
  cancelled: 'red',
};

export default function DeliveryPage() {
  const { token = '' } = useParams();
  const { data: order, isLoading, isFetching, refetch, error } = useDeliveryOrder(token);
  const transition = useDeliveryTransition(token);
  const [actionError, setActionError] = useState('');

  const submit = useCallback(async (action: 'dispatch' | 'deliver', expectedStage: FulfillmentStage) => {
    setActionError('');
    try {
      await transition.mutateAsync({ action, expectedStage });
      await refetch();
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'No se pudo actualizar el pedido.');
      await refetch();
    }
  }, [refetch, transition]);

  if (isLoading) return <LoadingScreen label="Abriendo enlace de reparto…" />;

  if (!order || error) {
    return (
      <main className="mx-auto grid min-h-screen max-w-xl place-items-center p-4">
        <Card className="text-center">
          <h1 className="text-2xl font-black text-slate-950">Enlace no disponible</h1>
          <p className="mt-2 text-sm text-slate-600">El enlace de reparto no es válido, fue revocado o el pedido ya finalizó.</p>
        </Card>
      </main>
    );
  }

  const canDispatch = order.stage === 'ready';
  const canDeliver = order.stage === 'transit';

  return (
    <main className="min-h-screen bg-slate-100 p-4">
      <div className="mx-auto grid max-w-2xl gap-4">
        <header className="rounded-3xl bg-slate-950 p-5 text-white shadow-lg">
          <p className="text-xs font-bold uppercase tracking-[.25em] text-slate-400">Reparto</p>
          <h1 className="mt-1 text-3xl font-black">Pedido #{String(order.correlation).padStart(2, '0')}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Badge tone={stageTone[order.stage]}>{stageLabels[order.stage]}</Badge>
            {isFetching && <span className="text-sm font-semibold text-slate-300">Actualizando…</span>}
          </div>
        </header>

        <Card className="grid gap-4">
          <section aria-labelledby="customer-heading">
            <h2 id="customer-heading" className="text-lg font-black text-slate-950">Contacto</h2>
            <p className="mt-1 text-sm text-slate-600">Cliente: <strong>{order.customerName}</strong></p>
            <p className="text-sm text-slate-600">Teléfono: <a className="font-bold text-sky-700" href={`tel:${order.customerPhone}`}>{order.customerPhone}</a></p>
          </section>

          {order.stage === 'transit' && order.confirmedMapsUrl && (
            <section aria-labelledby="location-heading">
              <h2 id="location-heading" className="text-lg font-black text-slate-950">Ubicación confirmada</h2>
              <a className="mt-2 inline-flex min-h-10 items-center justify-center rounded-xl bg-sky-600 px-4 py-2 text-sm font-bold text-white hover:bg-sky-700" href={order.confirmedMapsUrl} target="_blank" rel="noreferrer">Abrir en Google Maps</a>
            </section>
          )}

          <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700"><strong>Estado actual:</strong> {stageLabels[order.stage]}</p>
          {actionError && <div role="alert" aria-live="assertive" className="rounded-xl bg-rose-50 p-3 text-sm font-bold text-rose-800">{actionError}</div>}

          <div className="grid gap-2 sm:grid-cols-2">
            <Button disabled={!canDispatch} busy={transition.isPending && canDispatch} onClick={() => void submit('dispatch', order.stage)}>Iniciar reparto</Button>
            <Button variant="success" disabled={!canDeliver} busy={transition.isPending && canDeliver} onClick={() => void submit('deliver', order.stage)}>Marcar entregado</Button>
          </div>
        </Card>
      </div>
    </main>
  );
}
